import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit2, Trash2, Building2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    razao_social: '',
    cnpj: '',
    inscricao_estadual: '',
    empresa_id: '',
    email: '',
    telefone: '',
    endereco: '',
    numero: '',
    cep: '',
    cidade: '',
    bairro: '',
    uf: '',
    observacoes: '',
    ativo: true
  });

  useEffect(() => {
    carregarClientes();
    carregarEmpresas();
  }, []);

  const carregarClientes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/clients?apenas_ativas=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarEmpresas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmpresas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const nomeEmpresa = (empresaId) => {
    const emp = empresas.find((e) => e.id === empresaId);
    if (!emp) return '—';
    return emp.razao_social || emp.nome_fantasia || emp.name || 'Empresa';
  };

  const enderecoCompleto = (c) => {
    const rua = [c.endereco, c.numero].filter(Boolean).join(', ');
    const bairro = c.bairro || '';
    const cidade = [c.cidade, c.uf].filter(Boolean).join(' - ');
    const cep = c.cep ? `CEP ${c.cep}` : '';
    return [rua, bairro, cidade, cep].filter(Boolean).join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.empresa_id) {
      alert('Selecione a empresa vinculada ao cliente');
      return;
    }
    try {
      const token = localStorage.getItem('token');

      if (editando) {
        await axios.put(
          `${API}/api/clients/${editando.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/api/clients`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setShowModal(false);
      setEditando(null);
      setFormData({ nome: '', razao_social: '', cnpj: '', inscricao_estadual: '', empresa_id: '', email: '', telefone: '', endereco: '', numero: '', cep: '', cidade: '', bairro: '', uf: '', observacoes: '', ativo: true });
      carregarClientes();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      alert(error.response?.data?.detail || 'Erro ao salvar cliente');
    }
  };

  const handleEditar = (cliente) => {
    setEditando(cliente);
    setFormData({
      nome: cliente.nome,
      razao_social: cliente.razao_social || '',
      cnpj: cliente.cnpj || '',
      inscricao_estadual: cliente.inscricao_estadual || '',
      empresa_id: cliente.empresa_id || '',
      email: cliente.email || '',
      telefone: cliente.telefone || '',
      endereco: cliente.endereco || '',
      numero: cliente.numero || '',
      cep: cliente.cep || '',
      cidade: cliente.cidade || '',
      bairro: cliente.bairro || '',
      uf: cliente.uf || '',
      observacoes: cliente.observacoes || '',
      ativo: cliente.ativo
    });
    setShowModal(true);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja realmente desativar este cliente?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/clients/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      carregarClientes();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      alert('Erro ao excluir cliente');
    }
  };

  const handleNovo = () => {
    setEditando(null);
    setFormData({ nome: '', razao_social: '', cnpj: '', inscricao_estadual: '', empresa_id: '', email: '', telefone: '', endereco: '', numero: '', cep: '', cidade: '', bairro: '', uf: '', observacoes: '', ativo: true });
    setShowModal(true);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Clientes</h1>
          <p className="text-gray-600 mt-1">
            Gerencie os clientes para as viagens
          </p>
        </div>
        <Button onClick={handleNovo} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Lista de Clientes */}
      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {clientes.map((cliente) => (
            <Card key={cliente.id} className={!cliente.ativo ? 'opacity-50' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    {cliente.nome}
                  </span>
                  {cliente.ativo ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Ativo
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      Inativo
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {cliente.razao_social && (
                    <p><strong>Razão social:</strong> {cliente.razao_social}</p>
                  )}
                  {cliente.cnpj && (
                    <p><strong>CNPJ:</strong> {cliente.cnpj}</p>
                  )}
                  {cliente.inscricao_estadual && (
                    <p><strong>Inscrição Estadual:</strong> {cliente.inscricao_estadual}</p>
                  )}
                  {cliente.endereco && (
                    <p><strong>Endereço:</strong> {enderecoCompleto(cliente)}</p>
                  )}
                  <p>
                    <strong>Empresa:</strong> {cliente.empresa_nome || nomeEmpresa(cliente.empresa_id)}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditar(cliente)}
                    className="flex-1"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  {cliente.ativo && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExcluir(cliente.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criar/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar Cliente' : 'Novo Cliente'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome Fantasia *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Transportadora XYZ"
                required
              />
            </div>

            <div>
              <Label htmlFor="razao_social">Razão Social</Label>
              <Input
                id="razao_social"
                value={formData.razao_social}
                onChange={(e) => setFormData({...formData, razao_social: e.target.value})}
                placeholder="Razão social do cliente"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div>
                <Label htmlFor="inscricao_estadual">Inscrição Estadual</Label>
                <Input
                  id="inscricao_estadual"
                  value={formData.inscricao_estadual}
                  onChange={(e) => setFormData({...formData, inscricao_estadual: e.target.value})}
                  placeholder="IE do cliente"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="empresa_id">Empresa *</Label>
              <select
                id="empresa_id"
                value={formData.empresa_id}
                onChange={(e) => setFormData({...formData, empresa_id: e.target.value})}
                required
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Selecione a empresa...</option>
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.razao_social || emp.nome_fantasia || emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="contato@cliente.com"
                />
              </div>
              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                  placeholder="(00) 0000-0000"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                value={formData.endereco}
                onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                placeholder="Rua, avenida..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  value={formData.numero}
                  onChange={(e) => setFormData({...formData, numero: e.target.value})}
                  placeholder="Nº"
                />
              </div>
              <div>
                <Label htmlFor="bairro">Bairro</Label>
                <Input
                  id="bairro"
                  value={formData.bairro}
                  onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                  placeholder="Bairro"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cep">CEP</Label>
                <Input
                  id="cep"
                  value={formData.cep}
                  onChange={(e) => setFormData({...formData, cep: e.target.value})}
                  placeholder="00.000-000"
                />
              </div>
              <div>
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  value={formData.cidade}
                  onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                  placeholder="Cidade"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="uf">UF</Label>
              <Input
                id="uf"
                value={formData.uf}
                onChange={(e) => setFormData({...formData, uf: e.target.value.toUpperCase()})}
                placeholder="SP"
                maxLength={2}
              />
            </div>

            <div>
              <Label htmlFor="observacoes">Observações (opcional)</Label>
              <textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                placeholder="Informações adicionais..."
                className="w-full px-3 py-2 border rounded-md min-h-[70px]"
              />
            </div>

            {editando && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="ativo" className="cursor-pointer">
                  Cliente ativo
                </Label>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Check className="h-4 w-4 mr-2" />
                {editando ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientesPage;
